/**
 * Filters Component - Web Component
 * Manages the filter panel UI and instant filtering for search/collection pages
 */

class FiltersComponent extends HTMLElement {
  constructor() {
    super()

    // Bind methods
    this.handleClose = this.handleClose.bind(this)
    this.handleOverlayClick = this.handleOverlayClick.bind(this)
    this.handleEscapeKey = this.handleEscapeKey.bind(this)

    // State
    this.filterUrls = {
      experiences: new Map(),
      destinations: new Map(),
      priceMin: null,
      priceMax: null,
    }

    // État swipe (mobile)
    this.touchStartY = 0
    this.touchCurrentY = 0
    this.isDragging = false
    this.SWIPE_THRESHOLD = 100 // Distance minimale pour fermer (px)
  }

  connectedCallback() {
    // Get required elements
    this.container = this.querySelector('[data-filters-container]')
    this.panel = this.querySelector('[data-filters-panel]')
    this.overlay = this.querySelector('[data-filters-overlay]')
    this.form = this.querySelector('[data-filters-form]')
    this.content =
      this.panel?.querySelector('[data-filters-content]') ||
      this.panel?.querySelector('.filters__content')
    this.footer = this.panel?.querySelector('.filters__footer')
    this.filterBadge = document.querySelector('[data-filter-count]')

    if (!this.container || !this.panel || !this.overlay) {
      console.error('Filters: Container, panel or overlay not found')
      return
    }

    // Fix viewport height for mobile Safari
    this.setViewportHeight()
    this._boundSetViewportHeight = this.setViewportHeight.bind(this)
    window.addEventListener('resize', this._boundSetViewportHeight)

    // Initialize
    this.init()
    // Update badge on load
    this.updateFilterBadge()
  }

  disconnectedCallback() {
    // Clean up viewport height listener
    if (this._boundSetViewportHeight) {
      window.removeEventListener('resize', this._boundSetViewportHeight)
    }

    // Clean up event listeners
    const closeBtn = this.panel?.querySelector('[data-filters-close]')
    if (closeBtn) {
      closeBtn.removeEventListener('click', this.handleClose)
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick)
    }

    document.removeEventListener('keydown', this.handleEscapeKey)

    // Clean up experience buttons
    this.panel?.querySelectorAll('[data-filter-experience]').forEach((btn) => {
      btn.removeEventListener('click', btn._filterHandler)
    })

    // Clean up destination toggles
    this.panel?.querySelectorAll('[data-destination-toggle]').forEach((btn) => {
      btn.removeEventListener('click', btn._toggleHandler)
    })

    // Clean up destination checkboxes
    this.panel
      ?.querySelectorAll('[data-destination-checkbox]')
      .forEach((checkbox) => {
        checkbox.removeEventListener('change', checkbox._changeHandler)
      })

    // Clean up price range inputs
    const minInput = this.panel?.querySelector('[data-range-min]')
    const maxInput = this.panel?.querySelector('[data-range-max]')
    if (minInput) minInput.removeEventListener('input', minInput._inputHandler)
    if (maxInput) maxInput.removeEventListener('input', maxInput._inputHandler)

    // Clean up apply button
    const applyBtn = this.panel?.querySelector('[data-filters-apply]')
    if (applyBtn) {
      applyBtn.removeEventListener('click', applyBtn._applyHandler)
    }

    // Clean up sort by radio buttons
    this.panel?.querySelectorAll('[data-sort-by-input]').forEach((radio) => {
      radio.removeEventListener('change', radio._sortByHandler)
    })

    // Clean up scroll shadow
    if (this.content && this._scrollHandler) {
      this.content.removeEventListener('scroll', this._scrollHandler)
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }
  }

  /**
   * Set viewport height CSS variable for mobile Safari
   * Fixes 100vh issue where the height doesn't account for address bar
   */
  setViewportHeight() {
    if (!this.panel) return
    const vh = window.innerHeight
    this.panel.style.setProperty('--real-vh', `${vh}px`)
  }

  init() {
    // Close button
    const closeBtn = this.panel.querySelector('[data-filters-close]')
    if (closeBtn) {
      closeBtn.addEventListener('click', this.handleClose)
    }

    // Overlay click
    this.overlay.addEventListener('click', this.handleOverlayClick)

    // ESC key
    document.addEventListener('keydown', this.handleEscapeKey)

    // Swipe pour fermer (mobile)
    this.panel?.addEventListener('touchstart', (e) => this.handleTouchStart(e))
    this.panel?.addEventListener('touchmove', (e) => this.handleTouchMove(e))
    this.panel?.addEventListener('touchend', () => this.handleTouchEnd())

    // Experience buttons
    this.panel.querySelectorAll('[data-filter-experience]').forEach((btn) => {
      // Store URLs for this experience
      const experience = btn.dataset.experience
      this.filterUrls.experiences.set(experience, {
        add: btn.dataset.filterUrl,
        remove: btn.dataset.filterRemoveUrl,
        active: btn.classList.contains('is-active'),
      })

      btn._filterHandler = (e) => this.toggleExperience(e.currentTarget)
      btn.addEventListener('click', btn._filterHandler)
    })

    // Price range sliders
    this.initPriceRange()

    // Destination accordions
    this.panel.querySelectorAll('[data-destination-toggle]').forEach((btn) => {
      btn._toggleHandler = (e) =>
        this.toggleDestination(e.currentTarget.closest('.filters__destination'))
      btn.addEventListener('click', btn._toggleHandler)
    })

    // Destination checkboxes
    this.panel
      .querySelectorAll('[data-destination-checkbox]')
      .forEach((checkbox) => {
        // Store URLs for this destination
        const destination = checkbox.value
        this.filterUrls.destinations.set(destination, {
          add: checkbox.dataset.filterUrl,
          remove: checkbox.dataset.filterRemoveUrl,
          active: checkbox.checked,
        })

        checkbox._changeHandler = (e) =>
          this.handleDestinationChange(e.currentTarget)
        checkbox.addEventListener('change', checkbox._changeHandler)
      })

    // Apply button
    const applyBtn = this.panel.querySelector('[data-filters-apply]')
    if (applyBtn) {
      applyBtn._applyHandler = () => this.close()
      applyBtn.addEventListener('click', applyBtn._applyHandler)
    }

    // Sort by radio buttons
    this.panel.querySelectorAll('[data-sort-by-input]').forEach((radio) => {
      radio._sortByHandler = (e) => this.handleSortByChange(e.currentTarget)
      radio.addEventListener('change', radio._sortByHandler)
    })

    // Scroll shadow management
    if (this.content && this.footer) {
      this.initScrollShadow()
    }

    // Initialize price range based on all products
    this.updatePriceRange()
  }

  /**
   * Initialise la gestion de l'ombre de scroll
   */
  initScrollShadow() {
    const checkScroll = () => {
      if (!this.content || !this.footer) return

      const canScroll = this.content.scrollHeight > this.content.clientHeight
      const isAtBottom =
        this.content.scrollHeight - this.content.scrollTop <=
        this.content.clientHeight + 1 // 1px de marge pour les arrondis

      if (canScroll && !isAtBottom) {
        this.footer.classList.add('has-scroll')
        this.footer.classList.remove('is-scrolled-to-bottom')
      } else if (isAtBottom) {
        this.footer.classList.remove('has-scroll')
        this.footer.classList.add('is-scrolled-to-bottom')
      } else {
        this.footer.classList.remove('has-scroll', 'is-scrolled-to-bottom')
      }
    }

    // Vérifier au chargement et après un délai pour laisser le temps au contenu de se charger
    checkScroll()
    setTimeout(checkScroll, 100)

    // Écouter uniquement les événements de scroll
    this.content.addEventListener('scroll', checkScroll, { passive: true })

    // Stocker pour le cleanup
    this._scrollHandler = checkScroll
  }

  handleClose() {
    this.close()
  }

  handleOverlayClick() {
    this.close()
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape' && this.container.classList.contains('is-active')) {
      this.close()
    }
  }

  open() {
    // Update viewport height for mobile Safari
    this.setViewportHeight()

    this.container.classList.add('is-active')
    document.body.style.overflow = 'hidden'

    // Focus management
    const firstFocusable = this.panel.querySelector('[data-filters-close]')
    if (firstFocusable) firstFocusable.focus()

    // Vérifier l'ombre de scroll après l'ouverture
    if (this.content && this.footer) {
      setTimeout(() => {
        if (this._scrollHandler) {
          this._scrollHandler()
        }
      }, 100)
    }
  }

  close() {
    this.container.classList.remove('is-active')
    document.body.style.overflow = ''

    // Réinitialiser la position du panel (swipe)
    this.panel.style.transform = ''
  }

  /**
   * Gère le début du swipe
   */
  handleTouchStart(event) {
    // Ne gérer le swipe que si on touche le handle ou le header
    const target = event.target
    const isHandle = target.closest('[data-swipe-handle]')
    const isHeader = target.closest('.filters__header')

    if (isHandle || isHeader) {
      this.touchStartY = event.touches[0].clientY
      this.isDragging = true
      this.panel.style.transition = 'none'
    }
  }

  /**
   * Gère le mouvement du swipe
   */
  handleTouchMove(event) {
    if (!this.isDragging) return

    this.touchCurrentY = event.touches[0].clientY
    const deltaY = this.touchCurrentY - this.touchStartY

    // Ne permettre que le swipe vers le bas
    if (deltaY > 0) {
      event.preventDefault()
      // Sur mobile, le panel vient du bas donc translateY(0), on ajoute translateY
      this.panel.style.transform = `translateY(${deltaY}px)`
    }
  }

  /**
   * Gère la fin du swipe
   */
  handleTouchEnd() {
    if (!this.isDragging) return

    const deltaY = this.touchCurrentY - this.touchStartY

    // Réactiver la transition
    this.panel.style.transition = ''

    // Fermer si le swipe dépasse le seuil
    if (deltaY > this.SWIPE_THRESHOLD) {
      this.close()
    } else {
      // Sinon, revenir à la position initiale
      this.panel.style.transform = ''
    }

    // Réinitialiser l'état
    this.isDragging = false
    this.touchStartY = 0
    this.touchCurrentY = 0
  }

  toggleExperience(btn) {
    const experience = btn.dataset.experience
    const isActive = btn.classList.contains('is-active')

    // Toggle visual state
    btn.classList.toggle('is-active')

    // Update stored state
    const urlData = this.filterUrls.experiences.get(experience)
    if (urlData) {
      urlData.active = !isActive
    }

    // Update badge
    this.updateFilterBadge()

    // Instant filtering in background
    const url = !isActive ? btn.dataset.filterUrl : btn.dataset.filterRemoveUrl
    if (url) {
      this.applyFilterInBackground(url)
    }
  }

  initPriceRange() {
    const minInput = this.panel.querySelector('[data-range-min]')
    const maxInput = this.panel.querySelector('[data-range-max]')

    if (!minInput || !maxInput) return

    const track = this.panel.querySelector('[data-range-track]')
    const minDisplay = this.panel.querySelector('[data-price-min-display]')
    const maxDisplay = this.panel.querySelector('[data-price-max-display]')
    const priceInputsContainer = this.panel.querySelector('[data-price-param]')

    // Get the price filter parameter name from Search & Discovery
    this.priceFilterParam =
      priceInputsContainer?.dataset.priceParam || 'filter.v.price'

    // Store default values (min/max range bounds)
    this.priceRangeDefaults = {
      min: parseInt(minInput.min),
      max: parseInt(minInput.max),
    }

    // Store initial values (what the user sees on load)
    this.priceRangeInitial = {
      min: parseInt(minInput.value),
      max: parseInt(maxInput.value),
    }

    // Debounce timer for price filter updates
    let priceFilterTimeout = null

    // Update visual track and display without triggering filter
    const updateTrackVisual = () => {
      const min = parseInt(minInput.value)
      const max = parseInt(maxInput.value)
      const rangeMin = parseInt(minInput.min)
      const rangeMax = parseInt(minInput.max)

      const leftPercent = ((min - rangeMin) / (rangeMax - rangeMin)) * 100
      const rightPercent = ((rangeMax - max) / (rangeMax - rangeMin)) * 100

      track.style.left = `${leftPercent}%`
      track.style.right = `${rightPercent}%`

      minDisplay.textContent = `Min ${min} €`
      maxDisplay.textContent = `Max ${max} €`

      // Store price values
      this.filterUrls.priceMin = min
      this.filterUrls.priceMax = max

      // Update badge
      this.updateFilterBadge()
    }

    // Update track and trigger filter
    const updateTrackAndFilter = () => {
      updateTrackVisual()

      // Clear existing timeout
      if (priceFilterTimeout) {
        clearTimeout(priceFilterTimeout)
      }

      // Apply price filter with debounce (wait 500ms after user stops dragging)
      priceFilterTimeout = setTimeout(() => {
        const min = parseInt(minInput.value)
        const max = parseInt(maxInput.value)
        this.applyPriceFilter(min, max)
      }, 500)
    }

    minInput._inputHandler = () => {
      if (parseInt(minInput.value) > parseInt(maxInput.value)) {
        minInput.value = maxInput.value
      }
      updateTrackAndFilter()
    }
    minInput.addEventListener('input', minInput._inputHandler)

    maxInput._inputHandler = () => {
      if (parseInt(maxInput.value) < parseInt(minInput.value)) {
        maxInput.value = minInput.value
      }
      updateTrackAndFilter()
    }
    maxInput.addEventListener('input', maxInput._inputHandler)

    // Initialize track visual on load without triggering filter
    updateTrackVisual()
  }

  applyPriceFilter(min, max) {
    // Build the price filter URL based on current URL
    const url = new URL(window.location.href)
    const params = url.searchParams

    // Get the parameter name from Search & Discovery
    const paramName = this.priceFilterParam || 'filter.v.price'

    // Remove existing price filter params (try all possible formats)
    params.delete(`${paramName}.gte`)
    params.delete(`${paramName}.lte`)
    params.delete('filter.v.price.gte')
    params.delete('filter.v.price.lte')
    params.delete('filter.p.price.gte')
    params.delete('filter.p.price.lte')

    // Add new price params only if they differ from the defaults
    if (min !== this.priceRangeDefaults.min) {
      params.set(`${paramName}.gte`, min)
    }
    if (max !== this.priceRangeDefaults.max) {
      params.set(`${paramName}.lte`, max)
    }

    // Apply the filter
    const filterUrl = url.pathname + '?' + params.toString()
    this.applyFilterInBackground(filterUrl)
  }

  toggleDestination(regionElement) {
    const toggle = regionElement.querySelector('[data-destination-toggle]')

    // Ne pas permettre l'ouverture si le header est désactivé
    if (toggle.disabled || toggle.classList.contains('is-disabled')) {
      return
    }

    const isOpen = regionElement.classList.contains('is-open')

    if (isOpen) {
      regionElement.classList.remove('is-open')
      toggle.setAttribute('aria-expanded', 'false')
    } else {
      regionElement.classList.add('is-open')
      toggle.setAttribute('aria-expanded', 'true')
    }
  }

  handleDestinationChange(checkbox) {
    const destination = checkbox.value

    // Update stored state
    const urlData = this.filterUrls.destinations.get(destination)
    if (urlData) {
      urlData.active = checkbox.checked
    }

    // Update badge
    this.updateFilterBadge()

    // Instant filtering in background
    const url = checkbox.checked
      ? checkbox.dataset.filterUrl
      : checkbox.dataset.filterRemoveUrl
    if (url) {
      this.applyFilterInBackground(url)
    }
  }

  handleSortByChange(radio) {
    const sortByValue = radio.value
    const url = new URL(window.location.href)
    url.searchParams.set('sort_by', sortByValue)

    this.updateFilterBadge()

    this.applyFilterInBackground(url.toString())
  }

  applyFilterInBackground(url) {
    // Fetch the filtered results in the background without closing the panel
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        // Use requestAnimationFrame to batch DOM updates and avoid reflows
        requestAnimationFrame(() => {
          // Parse the HTML response
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')

          // Detect if we're on a search or collection page
          const isSearchPage = !!document.querySelector('.search-results__grid')

          // Use appropriate selectors based on page type
          const resultsSelector = isSearchPage
            ? '.search-results__grid'
            : '.collection-results__grid'
          const countSelector = isSearchPage
            ? '.search-info__count-value'
            : null // Collections don't have a visible count in the same place

          // Find the results section in the fetched page
          const newResults = doc.querySelector(resultsSelector)
          const currentResults = document.querySelector(resultsSelector)

          if (newResults && currentResults) {
            // Replace the current results with the new ones
            currentResults.innerHTML = newResults.innerHTML

            // Update the result count (search pages only)
            if (countSelector) {
              const newCount = doc.querySelector(countSelector)
              const currentCount = document.querySelector(countSelector)
              if (newCount && currentCount) {
                currentCount.textContent = newCount.textContent
              }
            }

            // Update pagination if it exists
            const newPagination = doc.querySelector('.pagination')
            const currentPagination = document.querySelector('.pagination')
            if (newPagination && currentPagination) {
              // Update existing pagination
              currentPagination.innerHTML = newPagination.innerHTML
            } else if (newPagination && !currentPagination) {
              // Re-insert pagination if it was removed previously
              const resultsContainer = currentResults.parentElement
              if (resultsContainer) {
                resultsContainer.appendChild(newPagination.cloneNode(true))
              }
            } else if (!newPagination && currentPagination) {
              // Remove pagination if no longer needed
              currentPagination.remove()
            }
          }

          // Update filter availability based on current results
          this.updateFilterAvailability(doc)

          // Update the browser URL without navigation
          window.history.pushState({}, '', url)
        })
      })
      .catch((error) => {
        console.error('Filter error:', error)
        // If fetch fails, fallback to normal navigation
        window.location.href = url
      })
  }

  /**
   * Update price range based on all products in the collection/search
   * Uses Shopify Search & Discovery filter data which includes ALL products, not just visible ones
   */
  updatePriceRange() {
    const currentPriceInputsContainer =
      this.panel.querySelector('[data-price-param]')

    if (!currentPriceInputsContainer) return

    const minInput = this.panel.querySelector('[data-range-min]')
    const maxInput = this.panel.querySelector('[data-range-max]')

    if (!minInput || !maxInput) return

    // Get range values from Shopify Search & Discovery filter
    // These values represent ALL products in the collection/search, including paginated ones
    const rangeMin = parseInt(currentPriceInputsContainer.dataset.priceRangeMin)
    const rangeMax = parseInt(currentPriceInputsContainer.dataset.priceRangeMax)

    if (isNaN(rangeMin) || isNaN(rangeMax)) return

    // Update the min/max attributes of the inputs
    minInput.min = rangeMin
    minInput.max = rangeMax
    minInput.value = rangeMin
    maxInput.min = rangeMin
    maxInput.max = rangeMax
    maxInput.value = rangeMax

    // Update the stored defaults
    this.priceRangeDefaults.min = rangeMin
    this.priceRangeDefaults.max = rangeMax

    // Update visual track and displays
    const track = this.panel.querySelector('[data-range-track]')
    const minDisplay = this.panel.querySelector('[data-price-min-display]')
    const maxDisplay = this.panel.querySelector('[data-price-max-display]')

    if (track) {
      track.style.left = '0%'
      track.style.right = '0%'
    }

    if (minDisplay) {
      minDisplay.textContent = `Min ${rangeMin} €`
    }

    if (maxDisplay) {
      maxDisplay.textContent = `Max ${rangeMax} €`
    }
  }

  updateFilterAvailability(doc) {
    // Update filter options based on the count provided by Shopify Search & Discovery
    // Get the updated filter values from the fetched page
    const newFiltersForm =
      doc.querySelector('[data-filters-form]') ||
      doc.querySelector('[data-filters-content]')

    if (!newFiltersForm) return

    // Update experience buttons based on count
    const newExperienceButtons = newFiltersForm.querySelectorAll(
      '[data-filter-experience]'
    )
    const currentExperienceButtons = this.panel.querySelectorAll(
      '[data-filter-experience]'
    )

    // Create a map of experience => count from the new form
    const experienceCounts = new Map()
    newExperienceButtons.forEach((btn) => {
      const experience = btn.dataset.experience
      const count = parseInt(btn.dataset.count) || 0
      experienceCounts.set(experience, count)
    })

    // Update current buttons
    let disabledExpCount = 0
    currentExperienceButtons.forEach((btn) => {
      const experience = btn.dataset.experience
      const count = experienceCounts.get(experience) || 0
      const isActive = btn.classList.contains('is-active')

      // Update the count attribute
      btn.dataset.count = count

      // Disable if count is 0 and not currently active
      if (count === 0 && !isActive) {
        btn.disabled = true
        btn.style.opacity = '0.5'
        btn.style.cursor = 'not-allowed'
        disabledExpCount++
      } else {
        btn.disabled = false
        btn.style.opacity = ''
        btn.style.cursor = ''
      }
    })

    // Update destination checkboxes based on count
    const newDestinationCheckboxes = newFiltersForm.querySelectorAll(
      '[data-destination-checkbox]'
    )
    const currentDestinationCheckboxes = this.panel.querySelectorAll(
      '[data-destination-checkbox]'
    )

    // Create a map of destination => count
    const destinationCounts = new Map()
    newDestinationCheckboxes.forEach((checkbox) => {
      const destination = checkbox.value
      const count = parseInt(checkbox.dataset.count) || 0
      destinationCounts.set(destination, count)
    })

    // Update current checkboxes
    let disabledDestCount = 0
    let reenabledDestCount = 0
    currentDestinationCheckboxes.forEach((checkbox) => {
      const destination = checkbox.value
      const count = destinationCounts.get(destination) || 0
      const isChecked = checkbox.checked
      const wasDisabled = checkbox.disabled

      // Update the count attribute
      checkbox.dataset.count = count

      // Disable if count is 0 and not currently checked
      if (count === 0 && !isChecked) {
        checkbox.disabled = true
        const wrapper = checkbox.closest('.filters__checkbox-wrapper')
        if (wrapper) wrapper.style.opacity = '0.5'
        disabledDestCount++
      } else {
        if (wasDisabled && count > 0) {
          reenabledDestCount++
        }
        checkbox.disabled = false
        const wrapper = checkbox.closest('.filters__checkbox-wrapper')
        if (wrapper) wrapper.style.opacity = ''
      }
    })

    // Vérifier chaque panneau de destination pour désactiver le header si toutes les destinations sont désactivées
    this.panel.querySelectorAll('.filters__destination').forEach((panel) => {
      const isOpen = panel.classList.contains('is-open')

      // Si le panneau n'est pas déplié
      if (!isOpen) {
        const checkboxes = panel.querySelectorAll('[data-destination-checkbox]')
        let allDisabled = true

        // Vérifier si toutes les checkboxes sont désactivées
        checkboxes.forEach((checkbox) => {
          if (!checkbox.disabled || checkbox.checked) {
            allDisabled = false
          }
        })

        const header = panel.querySelector('.filters__destination-header')

        if (allDisabled && checkboxes.length > 0) {
          // Désactiver le header
          header.disabled = true
          header.classList.add('is-disabled')
          header.style.cursor = 'not-allowed'

          // Réactiver visuellement les items (enlever l'opacité)
          checkboxes.forEach((checkbox) => {
            const wrapper = checkbox.closest('.filters__checkbox-wrapper')
            if (wrapper) wrapper.style.opacity = ''
          })
        } else {
          // Réactiver le header
          header.disabled = false
          header.classList.remove('is-disabled')
          header.style.cursor = ''
        }
      } else {
        // Si le panneau est déplié, toujours s'assurer que le header n'est pas désactivé
        const header = panel.querySelector('.filters__destination-header')
        if (header) {
          header.disabled = false
          header.classList.remove('is-disabled')
          header.style.cursor = ''
        }
      }
    })
  }

  updateFilterBadge() {
    if (!this.filterBadge) return

    // Count active filters
    let count = 0

    // Count active experiences
    this.filterUrls.experiences.forEach((data) => {
      if (data.active) count++
    })

    // Count active destinations
    this.filterUrls.destinations.forEach((data) => {
      if (data.active) count++
    })

    // Count price filter if it's different from initial values
    if (this.priceRangeInitial) {
      const priceMin = this.filterUrls.priceMin
      const priceMax = this.filterUrls.priceMax
      const isPriceFilterActive =
        priceMin !== this.priceRangeInitial.min ||
        priceMax !== this.priceRangeInitial.max

      if (isPriceFilterActive) count++
    }

    // Update badge
    if (count > 0) {
      this.filterBadge.textContent = count
      this.filterBadge.removeAttribute('hidden')
    } else {
      this.filterBadge.setAttribute('hidden', '')
    }
  }
}

// Define the custom element
if (!customElements.get('filters-component')) {
  customElements.define('filters-component', FiltersComponent)
}

// Global event listener for filter open buttons
document.addEventListener('click', (e) => {
  const openBtn = e.target.closest('[data-filters-open]')
  if (openBtn) {
    e.preventDefault()
    const filtersComponent = document.querySelector('filters-component')
    if (filtersComponent) {
      filtersComponent.open()
    }
  }
})
